﻿import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Card, Radio, Alert, Table, Spin } from "antd";
import dayjs from "dayjs";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  ComposedChart,
  Scatter,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ReferenceLine,
} from "recharts";
import "./Styles.css";

const EVALS = [
  { key: "sppb", label: "SPPB" },
  { key: "sts30", label: "30 sec" },
  { key: "sts5x", label: "5 rep" },
  { key: "equil", label: "Equil" },
  { key: "walk4min", label: "4 min" },
  { key: "frt", label: "FRT" },
];

const PROGRAM_FOLLOWUP = [
  { key: "walkingTime", label: "Tps marche" },
  { key: "exercises", label: "Exercices" },
  { key: "difficulty", label: "Difficulte" },
  { key: "pain", label: "Douleur" },
];

function fmt(v, decimals = 2) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(decimals) : "N/A";
}

function fmtBadge(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "N/A";
  return n.toFixed(1).replace(".", ",");
}

function getWalkSpeedClass(speed) {
  const n = Number(speed);
  if (!Number.isFinite(n)) return "unknown";
  if (n >= 1.2) return "very-good";
  if (n >= 1) return "good";
  if (n >= 0.8) return "risk";
  return "high-risk";
}

const ticksFromDomain = (domain, tickCount = 7) => {
  const [rawMin, rawMax] = Array.isArray(domain) ? domain : [0, 1];
  const min = Number(rawMin);
  const max = Number(rawMax);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 1];
  if (min === max) return Array.from(new Set([min, 0])).sort((a, b) => a - b);
  const safeCount = Math.max(2, tickCount);
  const step = (max - min) / (safeCount - 1);
  const ticks = [];
  for (let i = 0; i < safeCount; i += 1) {
    ticks.push(Number((min + step * i).toFixed(2)));
  }
  if (!ticks.some((t) => Math.abs(t) < 0.000001)) ticks.push(0);
  return Array.from(new Set(ticks)).sort((a, b) => a - b);
};

function buildSamples(points) {
  const raw = Array.isArray(points)
    ? points.map(Number).filter((n) => Number.isFinite(n))
    : [];
  return raw;
}

function toScatter(values, phaseX) {
  return values.map((v, idx) => ({
    x: phaseX + (((idx % 5) - 2) * 0.03),
    y: v,
  }));
}

function getDeltaClass(value) {
  if (value == null) return "unknown";
  return value >= 0 ? "good" : "high-risk";
}

function getEquilRiskLabel(lastScore) {
  if (lastScore == null) return "N/A";
  return lastScore >= 5 ? "normal" : "a risque";
}

function getWalkBarFill(entry) {
  if (entry.name === "Delta") return entry.value >= 0 ? "#2e7d32" : "#c62828";
  if (entry.cls === "very-good") return "#1b5e20";
  if (entry.cls === "good") return "#2e7d32";
  if (entry.cls === "risk") return "#ef6c00";
  if (entry.cls === "high-risk") return "#c62828";
  return "#94a3b8";
}

export default function AllPatientsSuiviEvaluationsCard({
  data = null,
  isLoading = false,
  sessions = [],
  isLoadingProgram = false,
  totalParticipants = null,
  totalNT0 = null,
  totalNT1 = null,
}) {
  // Onglet actif des métriques d'évaluation (global)
  const [activeEval, setActiveEval] = useState("sppb");
  // Onglet actif des métriques de suivi programme (global)
  const [activeProgramMetric, setActiveProgramMetric] = useState("walkingTime");

  const evalData = data?.[activeEval] ?? null;

  const tableColumns = useMemo(
    () => [
      { title: "T0", dataIndex: "t0", key: "t0", width: 120 },
      { title: "T1", dataIndex: "t1", key: "t1", width: 120 },
      { title: "delta", dataIndex: "delta", key: "delta", width: 120 },
    ],
    []
  );

  const tableData = useMemo(
    () => [
      {
        key: "row",
        t0: fmt(evalData?.t0Mean),
        t1: fmt(evalData?.t1Mean),
        delta: fmt(evalData?.deltaMean),
      },
    ],
    [evalData]
  );

  const evalTrendData = useMemo(() => {
    const t0 = Number(evalData?.t0Mean);
    const t1 = Number(evalData?.t1Mean);
    const rows = [];
    if (Number.isFinite(t0)) rows.push({ phase: "T0", value: t0 });
    if (Number.isFinite(t1)) rows.push({ phase: "T1", value: t1 });
    return rows;
  }, [evalData]);

  const evalTrendDomain = useMemo(() => {
    if (!evalTrendData.length) return [0, 1];
    const values = evalTrendData
      .map((row) => Number(row.value))
      .filter((n) => Number.isFinite(n));
    if (!values.length) return [0, 1];
    const min = Math.min(0, Math.floor(Math.min(...values)));
    const max = Math.max(1, Math.ceil(Math.max(...values)), 0);
    return [min, max];
  }, [evalTrendData]);

  const sppbDashboard = useMemo(() => {
    const t0Values = buildSamples(evalData?.t0Values);
    const t1Values = buildSamples(evalData?.t1Values);

    const all = [...t0Values, ...t1Values];
    const min = all.length ? Math.min(...all) : 0;
    const max = all.length ? Math.max(...all) : 14;
    const yMin = Math.min(0, Math.floor(min - 2));
    const yMax = Math.max(0, Math.min(14, Math.ceil(max + 2)));

    return {
      t0Points: toScatter(t0Values, 0),
      t1Points: toScatter(t1Values, 1),
      t0Mean: Number.isFinite(Number(evalData?.t0Mean)) ? Number(evalData?.t0Mean) : null,
      t1Mean: Number.isFinite(Number(evalData?.t1Mean)) ? Number(evalData?.t1Mean) : null,
      yMin,
      yMax: yMax > yMin ? yMax : yMin + 2,
    };
  }, [evalData]);

  const repetitionDashboard = useMemo(() => {
    const t0Values = buildSamples(evalData?.t0Values);
    const t1Values = buildSamples(evalData?.t1Values);

    const all = [...t0Values, ...t1Values];
    const min = all.length ? Math.min(...all) : 6;
    const max = all.length ? Math.max(...all) : 16;
    const yMin = Math.min(0, Math.floor(min - 2));
    const yMax = Math.max(yMin + 2, Math.ceil(max + 2));

    return {
      t0Points: toScatter(t0Values, 0),
      t1Points: toScatter(t1Values, 1),
      t0Mean: Number.isFinite(Number(evalData?.t0Mean)) ? Number(evalData?.t0Mean) : null,
      t1Mean: Number.isFinite(Number(evalData?.t1Mean)) ? Number(evalData?.t1Mean) : null,
      yMin,
      yMax,
    };
  }, [evalData]);

  const sts30RiskSummary = useMemo(() => {
    if (activeEval !== "sts30") return null;
    const r0 = evalData?.riskT0;
    const r1 = evalData?.riskT1;
    if (!r0 && !r1) return null;

    const line = (label, risk) => {
      if (!risk || !Number.isFinite(Number(risk.known))) return `${label}: N/A`;
      const known = Number(risk.known) || 0;
      const atRisk = Number(risk.atRisk) || 0;
      const notAtRisk = Number(risk.notAtRisk) || 0;
      return `${label}: à risque n=${atRisk} | non à risque n=${notAtRisk} | évaluables n=${known}`;
    };

    return `${line("T0", r0)} || ${line("T1", r1)}`;
  }, [activeEval, evalData]);

  const walkDashboard = useMemo(() => {
    const t0 = Number(evalData?.t0Mean);
    const t1 = Number(evalData?.t1Mean);
    const delta = Number(evalData?.deltaMean);

    const normalize = (v) => (Number.isFinite(v) ? v : null);
    const t0n = normalize(t0);
    const t1n = normalize(t1);
    const deltan = normalize(delta);
    const values = [t0n, t1n, deltan].filter((v) => Number.isFinite(v));
    const maxAbs = values.length ? Math.max(...values.map((v) => Math.abs(v))) : 2;
    const domainMax = Math.max(1.5, Math.ceil(maxAbs + 0.5));

    return {
      hasAny: values.length > 0,
      chartData: [
        { name: "T0", value: t0n, cls: getWalkSpeedClass(t0n) },
        { name: "T1", value: t1n, cls: getWalkSpeedClass(t1n) },
        { name: "Delta", value: deltan, cls: getDeltaClass(deltan) },
      ],
      domain: [-domainMax, domainMax],
    };
  }, [evalData]);

  const equilibrioDashboard = useMemo(() => {
    const t0 = Number(evalData?.t0Mean);
    const t1 = Number(evalData?.t1Mean);
    const delta = Number(evalData?.deltaMean);
    const norm = (v) => (Number.isFinite(v) ? v : null);
    const t0n = norm(t0);
    const t1n = norm(t1);
    const deltan = norm(delta);

    const hasUnipodal = Number.isFinite(t0n) || Number.isFinite(t1n);
    let lastPhase = "N/A";
    if (Number.isFinite(t1n)) lastPhase = "T1";
    else if (Number.isFinite(t0n)) lastPhase = "T0";
    const lastScore = Number.isFinite(t1n) ? t1n : t0n;
    const riskLabel = getEquilRiskLabel(lastScore);
    const values = [t0n, t1n, deltan].filter((v) => Number.isFinite(v));
    const maxAbs = values.length ? Math.max(...values.map((v) => Math.abs(v))) : 6;
    const domainMax = Math.max(6, Math.ceil(maxAbs + 1));

    return {
      hasUnipodal,
      lastPhase,
      lastScore,
      riskLabel,
      chartData: [
        { name: "T0", value: t0n },
        { name: "T1", value: t1n },
        { name: "Delta", value: deltan },
      ],
      domain: [-domainMax, domainMax],
    };
  }, [evalData]);

  const evalStatus = useMemo(() => {
    // Compare nT0/nT1 au nombre total de participants
    const participants = Number.isFinite(Number(totalParticipants))
      ? Number(totalParticipants)
      : Number(evalData?.nParticipants) || 0;
    const nT0 = Number.isFinite(Number(totalNT0))
      ? Number(totalNT0)
      : Number(evalData?.nT0) || 0;
    const nT1 = Number.isFinite(Number(totalNT1))
      ? Number(totalNT1)
      : Number(evalData?.nT1) || 0;

    const allT0Done = participants > 0 && nT0 >= participants;
    const allT1Done = participants > 0 && nT1 >= participants;

    return {
      participants,
      nT0,
      nT1,
      allT0Done,
      allT1Done,
      t0Label: `T0: ${nT0}/${participants} patients ont complété la première évaluation${
        allT0Done ? " (tous les patients)" : ""
      }`,
      t1Label: `T1: ${nT1}/${participants} patients ont complété la deuxième évaluation${
        allT1Done ? " (tous les patients)" : ""
      }`,
    };
  }, [evalData, totalParticipants, totalNT0, totalNT1]);

  const programStats = useMemo(() => {
    // Résumé global des sessions selon la métrique choisie
    const list = Array.isArray(sessions) ? sessions : [];
    const weekKeys = new Set(
      list
        .map((s) => s?.date)
        .filter(Boolean)
        .map((d) => dayjs(d).startOf("week").format("YYYY-MM-DD"))
    );

    const pick = (key) =>
      list
        .map((s) => Number(s?.[key]))
        .filter((n) => Number.isFinite(n));

    const valuesByMetric = {
      walkingTime: pick("walkingTime"),
      difficulty: pick("difficultyLevel"),
      pain: pick("painLevel"),
      exercises: list
        .map((s) => Number(s?.accomplishedExercice))
        .filter((n) => Number.isFinite(n)),
    };

    const values = valuesByMetric[activeProgramMetric] || [];
    const sum = values.reduce((a, b) => a + b, 0);
    const avg = values.length ? sum / values.length : null;
    const min = values.length ? Math.min(...values) : null;
    const max = values.length ? Math.max(...values) : null;

    return {
      weeks: weekKeys.size,
      avg,
      min,
      max,
      count: values.length,
    };
  }, [sessions, activeProgramMetric]);

  const programTrendData = useMemo(() => {
    const list = Array.isArray(sessions) ? sessions : [];
    const metricKeyByTab = {
      walkingTime: "walkingTime",
      difficulty: "difficultyLevel",
      pain: "painLevel",
      exercises: "accomplishedExercice",
    };
    const metricKey = metricKeyByTab[activeProgramMetric];
    const byDate = new Map();

    list.forEach((s) => {
      if (!s?.date || !metricKey) return;
      const raw = Number(s?.[metricKey]);
      if (!Number.isFinite(raw)) return;

      const dateKey = dayjs(s.date).format("YYYY-MM-DD");
      if (!byDate.has(dateKey)) byDate.set(dateKey, []);
      byDate.get(dateKey).push(raw);
    });

    return Array.from(byDate.entries())
      .map(([date, values]) => ({
        date,
        value: values.reduce((a, b) => a + b, 0) / values.length,
      }))
      .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));
  }, [sessions, activeProgramMetric]);

  const programTrendDomain = useMemo(() => {
    if (!programTrendData.length) return [0, 1];
    const values = programTrendData
      .map((row) => Number(row.value))
      .filter((n) => Number.isFinite(n));
    if (!values.length) return [0, 1];
    const min = Math.min(0, Math.floor(Math.min(...values)));
    const max = Math.max(1, Math.ceil(Math.max(...values)), 0);
    return [min, max];
  }, [programTrendData]);

  const renderEvalChart = () => {
    switch (activeEval) {
      case "sppb":
        return (
          <div className="sppb-dashboard">
            <div className="sppb-meta">
              <div className="sppb-meta-col">
                <div className="sppb-meta-title">Score</div>
                <div>0-6</div>
                <div>7-9</div>
                <div>10-12</div>
              </div>
              <div className="sppb-meta-col">
                <div className="sppb-meta-title">Interpretation</div>
                <div className="sppb-meta-row">
                  <span className="sppb-dot sppb-dot--low" />{" "}
                  Faible performance
                </div>
                <div className="sppb-meta-row">
                  <span className="sppb-dot sppb-dot--moderate" />{" "}
                  Performance moderee
                </div>
                <div className="sppb-meta-row">
                  <span className="sppb-dot sppb-dot--good" />{" "}
                  Bonne performance
                </div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  dataKey="x"
                  domain={[-0.5, 1.5]}
                  ticks={[0, 1]}
                  tickFormatter={(v) => (v === 0 ? "T0" : "T1")}
                />
                <YAxis
                  domain={[sppbDashboard.yMin, sppbDashboard.yMax]}
                  ticks={ticksFromDomain([sppbDashboard.yMin, sppbDashboard.yMax])}
                  interval={0}
                />
                <ReferenceLine y={0} stroke="#7f8c8d" strokeDasharray="4 4" />
                <Tooltip
                  formatter={(value) =>
                    Number.isFinite(Number(value)) ? Number(value).toFixed(2) : value
                  }
                  labelFormatter={(label) => (Number(label) === 0 ? "T0" : "T1")}
                />
                <Scatter data={sppbDashboard.t0Points} fill="#2b6cb0" name="SPPB T0" />
                <Scatter data={sppbDashboard.t1Points} fill="#d97706" name="SPPB T1" />
                {sppbDashboard.t0Mean != null && (
                  <ReferenceLine
                    segment={[
                      { x: -0.18, y: sppbDashboard.t0Mean },
                      { x: 0.18, y: sppbDashboard.t0Mean },
                    ]}
                    stroke="#2b6cb0"
                    strokeWidth={3}
                  />
                )}
                {sppbDashboard.t1Mean != null && (
                  <ReferenceLine
                    segment={[
                      { x: 0.82, y: sppbDashboard.t1Mean },
                      { x: 1.18, y: sppbDashboard.t1Mean },
                    ]}
                    stroke="#d97706"
                    strokeWidth={3}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
            <div className="sppb-badges">
              <span className="sppb-badge sppb-badge--t0">{fmtBadge(sppbDashboard.t0Mean)}</span>
              <span className="sppb-badge sppb-badge--t1">{fmtBadge(sppbDashboard.t1Mean)}</span>
            </div>
          </div>
        );
      case "sts30":
      case "sts5x":
        return (
          <div className="sppb-dashboard">
            <div className="sts30-title">
              {activeEval === "sts30"
                ? "30 seconde nombre de lever (tous les patients)"
                : "5 rep assis debout (tous les patients)"}
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  dataKey="x"
                  domain={[-0.5, 1.5]}
                  ticks={[0, 1]}
                  tickFormatter={(v) => (v === 0 ? "T0" : "T1")}
                />
                <YAxis
                  domain={[repetitionDashboard.yMin, repetitionDashboard.yMax]}
                  ticks={ticksFromDomain([repetitionDashboard.yMin, repetitionDashboard.yMax])}
                  interval={0}
                />
                <ReferenceLine y={0} stroke="#7f8c8d" strokeDasharray="4 4" />
                <Tooltip
                  formatter={(value) =>
                    Number.isFinite(Number(value)) ? Number(value).toFixed(2) : value
                  }
                  labelFormatter={(label) => (Number(label) === 0 ? "T0" : "T1")}
                />
                <Scatter
                  data={repetitionDashboard.t0Points}
                  fill="#2b6cb0"
                  name={activeEval === "sts30" ? "30s T0" : "5rep T0"}
                />
                <Scatter
                  data={repetitionDashboard.t1Points}
                  fill="#d97706"
                  name={activeEval === "sts30" ? "30s T1" : "5rep T1"}
                />
                {repetitionDashboard.t0Mean != null && (
                  <ReferenceLine
                    segment={[
                      { x: -0.18, y: repetitionDashboard.t0Mean },
                      { x: 0.18, y: repetitionDashboard.t0Mean },
                    ]}
                    stroke="#2b6cb0"
                    strokeWidth={3}
                  />
                )}
                {repetitionDashboard.t1Mean != null && (
                  <ReferenceLine
                    segment={[
                      { x: 0.82, y: repetitionDashboard.t1Mean },
                      { x: 1.18, y: repetitionDashboard.t1Mean },
                    ]}
                    stroke="#d97706"
                    strokeWidth={3}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
            <div className="sppb-badges">
              <span className="sppb-badge sppb-badge--t0">{fmtBadge(repetitionDashboard.t0Mean)}</span>
              <span className="sppb-badge sppb-badge--t1">{fmtBadge(repetitionDashboard.t1Mean)}</span>
            </div>
          </div>
        );
      case "walk4min":
        return (
          <div className="sts30-dashboard">
            <div className="sts30-title">Temps de marche (4MN) - tous les patients</div>
            <div className="walk-meta">
              <span className="walk-chip walk-chip--very-good">&gt;=1,2 m/s</span>
              <span className="walk-chip walk-chip--good">1 a &lt;1,2 m/s</span>
              <span className="walk-chip walk-chip--risk">&lt;1 m/s</span>
              <span className="walk-chip walk-chip--high-risk">&lt;0,8 m/s</span>
            </div>
            {walkDashboard.hasAny ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={walkDashboard.chartData} margin={{ top: 8, right: 20, bottom: 20, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis domain={walkDashboard.domain} ticks={ticksFromDomain(walkDashboard.domain)} interval={0} />
                  <ReferenceLine y={0} stroke="#7f8c8d" strokeDasharray="4 4" />
                  <Tooltip formatter={(value) => (Number.isFinite(Number(value)) ? Number(value).toFixed(2) : "N/A")} />
                  <Legend />
                  <ReferenceLine y={1} stroke="#4f83d1" strokeDasharray="5 5" label="1 m/s" />
                  <ReferenceLine y={0.8} stroke="#4f83d1" strokeDasharray="5 5" label="0,8 m/s" />
                  <Bar dataKey="value" name="Vitesse / delta">
                    {walkDashboard.chartData.map((entry) => {
                      const fill = getWalkBarFill(entry);
                      return <Cell key={`walk-global-bar-${entry.name}`} fill={fill} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="chart-placeholder">Aucune valeur de marche disponible.</div>
            )}
          </div>
        );
      case "equil":
        return (
          <div className="sts30-dashboard">
            <div className="sts30-title">Equilibre unipodal - tous les patients</div>
            <div className="equil-meta">
              <div className="equil-meta-box">
                <strong>{equilibrioDashboard.lastPhase}</strong>
                <span>
                  {equilibrioDashboard.lastScore == null
                    ? "N/A"
                    : `${equilibrioDashboard.lastScore.toFixed(1)} s`}
                </span>
              </div>
              <div className="equil-meta-box">
                <strong>Statut</strong>
                <span>{equilibrioDashboard.riskLabel}</span>
              </div>
              <div className="equil-meta-box">
                <strong>Seuil</strong>
                <span>5 s</span>
              </div>
            </div>
            {equilibrioDashboard.hasUnipodal ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={equilibrioDashboard.chartData}
                  margin={{ top: 8, right: 20, bottom: 20, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis
                    domain={equilibrioDashboard.domain}
                    ticks={ticksFromDomain(equilibrioDashboard.domain)}
                    interval={0}
                  />
                  <ReferenceLine y={0} stroke="#7f8c8d" strokeDasharray="4 4" />
                  <Tooltip
                    formatter={(value) =>
                      Number.isFinite(Number(value)) ? Number(value).toFixed(2) : "N/A"
                    }
                  />
                  <Legend />
                  <ReferenceLine y={5} stroke="#4f83d1" strokeDasharray="5 5" label="5 s" />
                  <Bar dataKey="value" name="Equilibre (sec)">
                    {equilibrioDashboard.chartData.map((entry) => {
                      let fill = "#94a3b8";
                      if (entry.name === "Delta") {
                        fill = entry.value >= 0 ? "#2e7d32" : "#c62828";
                      } else if (Number.isFinite(entry.value)) {
                        fill = entry.value >= 5 ? "#2e7d32" : "#ef6c00";
                      }
                      return <Cell key={`equil-global-bar-${entry.name}`} fill={fill} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="chart-placeholder">
                Graphe disponible uniquement si la position unipodale a ete faite.
              </div>
            )}
          </div>
        );
      default:
        return evalTrendData.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={evalTrendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="phase" />
              <YAxis
                domain={evalTrendDomain}
                ticks={ticksFromDomain(evalTrendDomain)}
                interval={0}
              />
              <ReferenceLine y={0} stroke="#7f8c8d" strokeDasharray="4 4" />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#1677ff"
                strokeWidth={2}
                dot={{ r: 4 }}
                name="Valeur"
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="chart-placeholder">Aucune valeur T0/T1 disponible.</div>
        );
    }
  };

  return (
    <div>
      <h2 className="section-header">Suivi evaluations</h2>

      <Card>
        <div className="paired-top-section">
          <Radio.Group
            className="chip-radio chip-radio--center chip-radio--eval"
            value={activeEval}
            onChange={(e) => setActiveEval(e.target.value)}
          >
            {EVALS.map((e) => (
              <Radio.Button key={e.key} value={e.key}>
                {e.label}
              </Radio.Button>
            ))}
          </Radio.Group>

          <div className="eval-content" style={{ marginTop: 16 }}>
            {isLoading ? (
              <div style={{ padding: 16, textAlign: "center" }}>
                <Spin />
              </div>
            ) : (
              <>
                <Alert
                  className="eval-status-alert"
                  type={evalStatus.allT0Done && evalStatus.allT1Done ? "success" : "warning"}
                  showIcon
                  message={evalStatus.t0Label}
                  description={evalStatus.t1Label}
                  style={{ marginBottom: 8 }}
                />
                <div style={{ marginBottom: 10 }}>
                  {`Participants=${evalData?.nParticipants ?? 0} | T0=${evalData?.nT0 ?? 0} | T1=${evalData?.nT1 ?? 0}`}
                </div>
                {sts30RiskSummary && (
                  <Alert
                    style={{ marginBottom: 10 }}
                    type="info"
                    showIcon
                    message={`30 sec assis-debout: ${sts30RiskSummary}`}
                  />
                )}
                <div className="eval-table-chart-row">
                  <Table
                    columns={tableColumns}
                    dataSource={tableData}
                    pagination={false}
                    size="small"
                  />
                  <div className="eval-trend-chart">
                    {renderEvalChart()}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="dashboard-subsection">
          <h3 className="dashboard-subsection-title">Suivi programme</h3>
          <Radio.Group
            className="chip-radio chip-radio--center chip-radio--program"
            value={activeProgramMetric}
            onChange={(e) => setActiveProgramMetric(e.target.value)}
          >
            {PROGRAM_FOLLOWUP.map((e) => (
              <Radio.Button key={e.key} value={e.key}>
                {e.label}
              </Radio.Button>
            ))}
          </Radio.Group>

          <div className="eval-content" style={{ marginTop: 16 }}>
            {isLoadingProgram ? (
              <div style={{ padding: 16, textAlign: "center" }}>
                <Spin />
              </div>
            ) : (
              <>
                <Alert
                  className="stats-program-alert"
                  type="info"
                  showIcon
                  message={
                    <div className="stats-program-title-wrap">
                      <span className="stats-program-title-label">Moyenne</span>
                      <span className="stats-program-title-value">{fmt(programStats.avg)}</span>
                    </div>
                  }
                  description={
                    <div className="stats-program-grid">
                      <div className="stats-program-pill">
                        <span className="stats-program-pill-label">Min</span>
                        <span className="stats-program-pill-value">{fmt(programStats.min)}</span>
                      </div>
                      <div className="stats-program-pill">
                        <span className="stats-program-pill-label">Max</span>
                        <span className="stats-program-pill-value">{fmt(programStats.max)}</span>
                      </div>
                      <div className="stats-program-pill">
                        <span className="stats-program-pill-label">Nombre de seances</span>
                        <span className="stats-program-pill-value">{programStats.count}</span>
                      </div>
                      <div className="stats-program-pill">
                        <span className="stats-program-pill-label">Nombre de semaines realisees</span>
                        <span className="stats-program-pill-value">{programStats.weeks}</span>
                      </div>
                    </div>
                  }
                />
                <div className="program-trend-chart">
                  {programTrendData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart
                        data={programTrendData}
                        margin={{ top: 8, right: 20, bottom: 30, left: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(tick) => dayjs(tick).format("DD/MM")}
                          angle={-35}
                          textAnchor="end"
                          height={55}
                        />
                        <YAxis
                          domain={programTrendDomain}
                          ticks={ticksFromDomain(programTrendDomain)}
                          interval={0}
                        />
                        <ReferenceLine y={0} stroke="#7f8c8d" strokeDasharray="4 4" />
                        <Tooltip labelFormatter={(label) => dayjs(label).format("DD/MM/YYYY")} />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke="#1677ff"
                          strokeWidth={2}
                          dot={{ r: 3 }}
                          name={PROGRAM_FOLLOWUP.find((m) => m.key === activeProgramMetric)?.label}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="chart-placeholder">Aucune session exploitable pour ce suivi.</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

AllPatientsSuiviEvaluationsCard.propTypes = {
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  sessions: PropTypes.array,
  isLoadingProgram: PropTypes.bool,
  totalParticipants: PropTypes.number,
  totalNT0: PropTypes.number,
  totalNT1: PropTypes.number,
};




